import { useState, useEffect, useCallback } from 'react';
import { userPrefsApi, type RecentSearch } from '@/services/userPrefsApi';

export function useRecentSearches() {
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const searches = await userPrefsApi.listRecentSearches();
      setRecentSearches(searches);
    } catch {
      // Silently fail — recent searches are non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const record = useCallback(async (query: string, resultCount: number, filters?: { repo?: string; type?: string; project?: string }) => {
    try {
      await userPrefsApi.recordSearch(query, resultCount, filters);
      // Refresh the list after recording
      const searches = await userPrefsApi.listRecentSearches();
      setRecentSearches(searches);
    } catch {
      // Fire-and-forget — don't block search UX
    }
  }, []);

  const dismiss = useCallback(async (searchId: string) => {
    // Optimistic removal
    setRecentSearches((prev) => prev.filter((s) => s.searchId !== searchId));
    try {
      await userPrefsApi.deleteRecentSearch(searchId);
    } catch {
      // Revert on failure
      fetch();
    }
  }, [fetch]);

  const clearAll = useCallback(async () => {
    setRecentSearches([]);
    try {
      await userPrefsApi.clearRecentSearches();
    } catch {
      fetch();
    }
  }, [fetch]);

  return { recentSearches, loading, record, dismiss, clearAll, refresh: fetch };
}
